import React, { useState } from 'react';
import { Field, withTypes } from 'react-final-form';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { Button, Spinner } from 'reactstrap';
import Icon from '../../../assets/icons/Icon';
import Timer from '../../../components/Timer';
import CustomInputField from '../../../components/form/CustomInputField';
import PreInvestmentScreeningModal from '../../../components/modal/PreInvestmentScreeningModal';
import { useKnowledgeTestDispatch } from '../../../contexts/KnowledgeTestContext';
import { useDict } from '../../../contexts/LanguageContext';
import ROUTE_NAME from '../../../routes/RouteName';
import { aesEncrypt } from '../../../utils/crypto';
import { postOTPLogin, postSendOTP } from '../src/authApis';

const TwoFactorAuthForm = () => {
    const navigate = useNavigate();

    const knowledgeTestDispatch = useKnowledgeTestDispatch();

    const dict = useDict();

    const [showPassword, setShowPassword] = useState(false);
    const [showPreInvestmentScreeningModal, setShowPreInvestmentScreeningModal] = useState(false);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isShowTimer, setIsShowTimer] = useState(false);
    const [resendCount, setResendCount] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [isSubmittedWealthDeclaration, setIsSubmittedWealthDeclaration] = useState(false);

    const twoFAValidate = (values) => {
        const errors = {};
        if (!values.code) {
            errors.code = dict.verificationCodeEmpty;
        }
        return errors;
    };

    const onShowPassword = (e) => {
        e.preventDefault();
        setShowPassword((prev) => !prev);
    };

    const handleSendOTP = () => {
        postSendOTP(handlePostSendOTP);
    }

    const handlePostSendOTP = (response) => {
        setIsShowTimer(true);
        setResendCount(resendCount + 1);

        toast.success(dict.resendOTPSuccessMessage, {
            duration: 5000,
            id: dict.resendOTPSuccessMessage,
        });
    };

    const handleSendOTPResponse = (response) => {
        setIsAuthenticated(true);

        sessionStorage.removeItem('login_token');
        sessionStorage.removeItem('email');

        sessionStorage.setItem('token_type', response.token_type);
        sessionStorage.setItem('access_token', aesEncrypt(response.access_token));
        sessionStorage.setItem('refresh_token', aesEncrypt(response.refresh_token));
        localStorage.setItem('language', response.locale);

        if (response.pre_screening_check) {
            if (response.wealth_declaration_check) {
                navigate(ROUTE_NAME.INVEST_NOW);
            } else {
                navigate(ROUTE_NAME.FIRST_TIME_DASHBOARD);
            }
        } else {
            if (response.wealth_declaration_check) {
                setIsSubmittedWealthDeclaration(true);
            }
            setShowPreInvestmentScreeningModal(true);
        }
    }

    const handleSubmit = (values) => {
        setIsLoading(true);

        values = {
            second_step: 1,
            ...values
        };

        try {
            postOTPLogin(values, handleSendOTPResponse);
        } finally {
            setTimeout(() => {
                setIsLoading(false);
            }, 1000);
        }
    };

    const { Form } = withTypes();

    return (
        <div>
            <Form
                validate={twoFAValidate}
                onSubmit={(values) => handleSubmit(values)}
            >
                {({ handleSubmit }) => (
                    <form className="form" onSubmit={handleSubmit}>
                        <div className="form__form-group">
                            <span
                                className="form__form-group-label"
                                style={{ textAlign: 'center', marginTop: '10px' }}
                            >
                                {dict.verificationText} {sessionStorage.getItem('email')}
                            </span>
                            <div className="form__form-group-field">
                                <Field name="code">
                                    {({ input, meta }) => (
                                        <CustomInputField
                                            input={input}
                                            meta={meta}
                                            type={
                                                showPassword ? 'text' : 'password'
                                            }
                                            placeholder={dict.sixDigits}
                                            data-cy="login__2fa"
                                        />
                                    )}
                                </Field>
                                <span
                                    className={`form__form-group-button${showPassword ? ' active' : ''
                                        }`}
                                    onClick={(e) => onShowPassword(e)}
                                    onKeyDown={(e) => onShowPassword(e)}
                                    role="button"
                                    tabIndex={0}
                                    style={{
                                        marginLeft: '-40px',
                                        background: 'none',
                                        border: 'none',
                                        marginTop: '3px',
                                    }}
                                >
                                    <Icon
                                        icon={showPassword ? 'Eye' : 'EyeSlash'}
                                    />
                                </span>
                            </div>
                        </div>
                        <div
                            className="account__btns"
                            style={{ marginLeft: '0px', marginBottom: 0, marginRight: '10px' }}
                        >
                            <Button
                                color="primary"
                                className="btn account__btn rounded"
                                type="submit"
                                style={{ margin: '0 0 0 10px', fontSize: '14px' }}
                                disabled={isLoading}
                                data-cy="login__submit2fa"
                            >
                                {isLoading ? (
                                    <Spinner size="sm" color="orange" />
                                ) : (
                                    dict.verify
                                )}
                            </Button>{' '}
                            &nbsp;
                        </div>
                        <div className="form__form-group" style={{ marginBottom: 0 }}>
                            <div className="text-center verify-text">
                                <span className="form__form-group-label">
                                    {dict.didNotGetVerificationCode}
                                </span>
                            </div>
                            <div
                                className="account__btns"
                                style={{ marginLeft: '0px', paddingRight: '5px' }}
                            >
                                <Button
                                    color="primary"
                                    className="rounded"
                                    outline
                                    disabled={isShowTimer || isLoading}
                                    onClick={handleSendOTP}
                                    style={{
                                        marginLeft: '10px',
                                        marginRight: '15px',
                                        fontSize: '12px',
                                        width: '100%',
                                    }}
                                >
                                    {dict.requestNewCode}
                                </Button>
                            </div>
                            <div
                                className="account__btns"
                                style={{
                                    marginTop: '15px',
                                    justifyContent: 'center',
                                }}
                            >
                                <Timer
                                    cooldownTime={60}
                                    maxResendCount={5}
                                    onCountdownToZero={() => setIsShowTimer(false)}
                                    conditionToStartTimer={isShowTimer}
                                    currentResendCount={resendCount}
                                />
                            </div>
                        </div>
                    </form>
                )}
            </Form>

            <PreInvestmentScreeningModal
                isOpen={showPreInvestmentScreeningModal}
                closeModal={() => setShowPreInvestmentScreeningModal(false)}
                isAuthenticated={isAuthenticated}
                isSubmittedWealthDeclaration={isSubmittedWealthDeclaration}
            />
        </div>
    );
};

export default TwoFactorAuthForm;
